import React, { useEffect, useState } from "react";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { useNavigate } from "react-router-dom";
import './addCustomItemFormStyles.css'; // Import the styles

const AddCustomItemForm = () => {
  // State management
  const [categories, setCategories] = useState({});
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedItem, setSelectedItem] = useState("");
  const [itemImg, setItemImg] = useState("");
  const [price, setPrice] = useState("");
  const [quantity, setQuantity] = useState("");
  const [freshlyBaked, setFreshlyBaked] = useState(false);
  const [ownerId, setOwnerId] = useState("");
  const [allCategories, setAllCategories] = useState([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [itemId, setItemId] = useState("");
  const navigate = useNavigate();
  const [categoryName, setCategoryName] = useState("");
  const [categoryDescription, setCategoryDescription] = useState("");
  const [categoryImage, setCategoryImage] = useState(null);
  const API_URL = process.env.REACT_APP_API_URL;

  // Fetch owner ID from token
  useEffect(() => {
    const token = localStorage.getItem("ownerToken");
    if (token) {
      try {
        const decodedToken = jwtDecode(token);
        setOwnerId(decodedToken.id);
      } catch (error) {
        console.error("Erreur lors du décodage du jeton:", error);
      }
    }
  }, []);

  // Fetch categories based on owner ID
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(
          `${API_URL}/api/cloneCategory/${ownerId}`
        );
        setCategories(response.data);
      } catch (error) {
        console.error("Erreur lors de la récupération des catégories:", error);
      }
    };

    if (ownerId) {
      fetchCategories();
    }
  }, [ownerId]);

  useEffect(() => {
    const fetchItems = async () => {
      try {
        const response = await axios.get(
          `${API_URL}/api/items/${ownerId}`
        );
        setAllCategories(response.data.items);
      } catch (error) {
        console.error("Erreur lors de la récupération des articles:", error);
      }
    };

    fetchItems();
  }, [ownerId]);

  // Handle category selection
  const handleCategoryChange = (e) => {
    setSelectedCategory(e.target.value);
    setSelectedItem("");
    setItemImg("");
  };

  // Handle item selection
  const handleItemChange = (e) => {
    const itemName = e.target.value;
    setSelectedItem(itemName);

    if (selectedCategory) {
      const selectedItemData = categories[selectedCategory]?.find(
        (item) => item.itemName === itemName
      );
      setItemImg(selectedItemData?.itemImg || "");
    }
  };

  // Handle image upload
  const handleUploadImage = async (e) => {
    const file = e.target.files[0];
    const formData = new FormData();
    formData.append("itemImg", file);

    try {
      const response = await axios.post(
        `${API_URL}/api/uploadImage`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );
      setItemImg(response.data.imageUrl);
    } catch (error) {
      console.error("Erreur lors du téléchargement de l'image:", error);
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    const itemData = {
      ownerId,
      category: selectedCategory,
      items: [
        {
          itemName: selectedItem,
          itemImg,
          price: parseFloat(price),
          quantity: parseInt(quantity, 10),
          freshlyBaked,
        },
      ],
    };

    try {
      const token = localStorage.getItem("ownerToken");
      if (!token) {
        alert("Veuillez vous connecter pour ajouter un article.");
        return;
      }

      await axios.post(
        `${API_URL}/api/customitems/add-item`,
        itemData,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      alert("Article ajouté avec succès!");
      window.location.reload();
      resetForm();
    } catch (error) {
      console.error("Erreur lors de l'ajout de l'article:", error);
      alert("Échec de l'ajout de l'article. Veuillez réessayer.");
    }
  };

  // Reset form fields
  const resetForm = () => {
    setSelectedCategory("");
    setSelectedItem("");
    setItemImg("");
    setPrice("");
    setQuantity("");
    setFreshlyBaked(false);
  };

  // Handle delete
  const handleDelete = async (category, itemId) => {
    const token = localStorage.getItem("ownerToken");
    console.log("token", token);
    if (!token) {
      alert("Veuillez vous connecter pour supprimer un article.");
      return;
    }

    const deleteData = {
      ownerId,
      category,
      itemId,
    };

    try {
      await axios.delete(`${API_URL}/api/customitems/delete-item`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data: deleteData,
      });

      alert("Article supprimé avec succès!");
      window.location.reload();
      const updatedCategories = allCategories.map((cat) =>
        cat.category === category
          ? {
              ...cat,
              items: cat.items.filter((item) => item._id !== itemId),
            }
          : cat
      );

      setAllCategories(updatedCategories);
    } catch (error) {
      console.error("Erreur lors de la suppression de l'article:", error);
      alert("Échec de la suppression de l'article. Veuillez réessayer.");
    }
  };

  // Handle edit
  const handleEdit = async (category, itemId) => {
    console.log("handleedit called");
    setIsEditMode(!isEditMode);

    // Find the category and item data from the frontend state
    const categoryData = allCategories.find((cat) => cat.category === category);
    const itemData = categoryData?.items.find((item) => item._id === itemId);

    if (itemData) {
      // Set form fields with existing data for editing
      setSelectedCategory(category);
      setItemId(itemId);
      setSelectedItem(itemData.itemName);
      setItemImg(itemData.itemImg);
      setPrice(itemData.price);
      setQuantity(itemData.quantity);
      setFreshlyBaked(itemData.freshlyBaked);

      // Prepare the data payload for the PUT request
    }
  };

  const handleUpdate = async (e) => {
    console.log("handleUpdate called");
    const token = localStorage.getItem("ownerToken");

    if (!token) {
      alert("Veuillez vous connecter pour modifier un article.");
      return;
    }

    const updateData = {
      ownerId, // Replace with the actual ownerId
      category: selectedCategory,
      itemId: itemId,
      updates: {
        itemImg, // Use form data if changes are allowed before sending
        price,
        quantity,
        freshlyBaked,
      },
    };

    try {
      // Send the PUT request to the backend
      const response = await axios.put(
        `${API_URL}/api/customitems/update-item`,
        updateData,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.status === 200) {
        alert("Article mis à jour avec succès!");
        // Optionally update the frontend state or reload the page
      } else {
        alert("Échec de la mise à jour de l'article. Veuillez réessayer.");
      }
    } catch (error) {
      console.error("Erreur lors de la mise à jour de l'article:", error);
      alert("Une erreur s'est produite lors de la mise à jour de l'article.");
    }
  };

  const handleOrderDetailsClick = () => {
    navigate("/ownerorders"); // Redirects to the /ownerorders route
  };
  const handleAddCategoryAndItems = () => {
    navigate("/addCategoryAndItems"); // Redirects to the /addCategoryAndItems route
  };

  return (
    <div className="container">
      {/* Header */}
      <header className="header">
        <div className="header-content">
          <div className="header-left">
            <div className="logo">Lememeprix</div>
          
          </div>
          <div className="header-right">
            <i className="fa-solid fa-cart-shopping cart-icon"></i>
            <img
              src="https://storage.googleapis.com/uxpilot-auth.appspot.com/avatars/avatar-3.jpg"
              alt="Profile"
              className="profile-image"
            />
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero">
        <div className="hero-overlay">
          <img
            className="hero-image"
            src="https://storage.googleapis.com/uxpilot-auth.appspot.com/692c6d9dba-4a4edb1b09f999249cf2.png"
            alt="professional food photography of various gourmet dishes on a restaurant table, warm lighting, cinematic style"
          />
        </div>
        <div className="hero-content">
          <h1 className="hero-title">Gérez votre menu</h1>
          <p className="hero-subtitle">Ajoutez des catégories et des articles facilement</p>
        </div>
        
      </section>

      {/* Add Custom Item Section */}
      {/* Button Section */}
      <div className="button-section">
          <button
            className="order-details-button"
            onClick={handleOrderDetailsClick}
          >
            Détails de la commande
          </button>
          <button
            className="add-category-items-button"
            onClick={handleAddCategoryAndItems}
          >
            Ajouter une catégorie et des articles
          </button>
        </div>
      <section className="add-custom-item">
        <h2 className="add-custom-item-title">Ajouter un article personnalisé</h2>
        <form className="add-custom-item-form">
          <div className="form-group">
            <label>Catégorie</label>
            <select
              value={selectedCategory}
              onChange={handleCategoryChange}
              required
            >
              <option value="">Sélectionner une catégorie</option>
              {Object.keys(categories).map((categoryName) => (
                <option key={categoryName} value={categoryName}>
                  {categoryName}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Nom de l'article</label>
            <select
              value={selectedItem}
              onChange={handleItemChange}
              disabled={!selectedCategory}
            >
              <option value="">Sélectionner un article</option>
              {selectedCategory &&
                categories[selectedCategory]?.map((item) => (
                  <option key={item._id} value={item.itemName}>
                    {item.itemName}
                  </option>
                ))}
            </select>
          </div>
        </form>

        {selectedCategory && selectedItem && (
          <form onSubmit={isEditMode ? handleUpdate : handleSubmit}>
            <table>
              <thead>
                <tr>
                  <th>Catégorie</th>
                  <th>Nom de l'article</th>
                  <th>Image de l'article</th>
                  <th>Prix</th>
                  <th>Quantité</th>
                  <th>Fraîchement cuit</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>{selectedCategory}</td>
                  <td>{selectedItem}</td>
                  <td>
                    {itemImg ? (
                      <div>
                        <img
                          src={itemImg}
                          alt="Article"
                          style={{ width: "100px" }}
                        />
                        <button type="button" onClick={() => setItemImg("")}>
                          Supprimer l'image
                        </button>
                      </div>
                    ) : (
                      <input
                        type="file"
                        name="itemImg"
                        onChange={handleUploadImage}
                      />
                    )}
                  </td>
                  <td>
                    <input
                      type="number"
                      value={price}
                      onChange={(e) => setPrice(e.target.value)}
                      placeholder="Entrez le prix"
                      required
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      value={quantity}
                      onChange={(e) => setQuantity(e.target.value)}
                      placeholder="Entrez la quantité"
                      required
                    />
                  </td>
                  <td>
                    <input
                      type="checkbox"
                      checked={freshlyBaked}
                      onChange={() => setFreshlyBaked(!freshlyBaked)}
                    />
                  </td>
                  <td>
                    <button type="submit">
                      {isEditMode ? "Mettre à jour" : "Enregistrer"}
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </form>
        )}
      </section>

      {/* Category List */}
      <section className="category-list">
        <h2 className="category-list-title">Vos catégories</h2>

        {allCategories.length === 0 ? (
          <p>Aucun article disponible.</p>
        ) : (
          allCategories.map((category) => (
            <div className="category-card" key={category._id}>
              <div className="category-info">
                <h3 className="category-name">{category.category}</h3>
                <p className="category-item-count">
                  {category.items.length} articles
                </p>
              </div>

              <table>
                <thead>
                  <tr>
                    <th>Catégorie</th>
                    <th>Nom de l'article</th>
                    <th>Image de l'article</th>
                    <th>Prix</th>
                    <th>Quantité</th>
                    <th>Fraîchement cuit</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {category.items &&
                    category.items.map((item) => (
                      <tr key={item._id}>
                        <td>{category.category}</td>
                        <td>{item.itemName}</td>
                        <td>
                          {item.itemImg ? (
                            <div>
                              <img
                                src={item.itemImg}
                                alt="Article"
                                style={{ width: "100px" }}
                              />
                              <button
                                type="button"
                                onClick={() => setItemImg("")}
                              >
                                Supprimer l'image
                              </button>
                            </div>
                          ) : (
                            <input
                              type="file"
                              name="itemImg"
                              onChange={handleUploadImage}
                            />
                          )}
                        </td>
                        <td>{item.price}</td>
                        <td>{item.quantity}</td>
                        <td>{item.freshlyBaked ? "Oui" : "Non"}</td>
                        <td>
                          <button
                            onClick={() =>
                              handleEdit(category.category, item._id)
                            }
                            style={{ marginRight: "5px" }}
                          >
                            Modifier
                          </button>
                          <button
                            onClick={() =>
                              handleDelete(category.category, item._id)
                            }
                          >
                            Supprimer
                          </button>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          ))
        )}
      </section>

     

      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">
          <div className="footer-section">
            <h3 className="footer-logo">Lememeprix</h3>
            <p className="footer-description">
              Votre plateforme de gestion alimentaire premium
            </p>
          </div>
          <div className="footer-section">
            <h4 className="footer-title">Liens rapides</h4>
            <div className="footer-links">
              <a href="#" className="footer-link">
                À propos de nous
              </a>
              <a href="#" className="footer-link">
                Contact
              </a>
              <a href="#" className="footer-link">
                Politique de confidentialité
              </a>
            </div>
          </div>
          <div className="footer-section">
            <h4 className="footer-title">Assistance</h4>
            <div className="footer-links">
              <a href="#" className="footer-link">
                Centre d'aide
              </a>
              <a href="#" className="footer-link">
                Conditions d'utilisation
              </a>
              <a href="#" className="footer-link">
                Juridique
              </a>
            </div>
          </div>
          <div className="footer-section">
            <h4 className="footer-title">Suivez-nous</h4>
            <div className="footer-socials">
              <a href="#">
                <i className="fa-brands fa-facebook"></i>
              </a>
              <a href="#">
                <i className="fa-brands fa-twitter"></i>
              </a>
              <a href="#">
                <i className="fa-brands fa-instagram"></i>
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AddCustomItemForm;